import styled from 'styled-components';
import { COLORS } from '../../../constants/radar';

export const HeaderStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HeaderStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  padding: 0 16px;

  .top {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  .label {
    font-size: 11px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  .value {
    font-size: 20px;
    font-weight: 700;
    color: ${COLORS.PRIMARY};
  }

  .sub {
    font-size: 9px;
    color: #94a3b8;
    line-height: 1;
  }
`;

export const HeaderButton = styled.button`
  padding: 8px 16px;
  background-color: white;
  color: ${COLORS.PRIMARY};
  border: 1px solid ${COLORS.PRIMARY};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  white-space: nowrap;

  &:hover {
    background-color: ${COLORS.PRIMARY};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const SourceNote = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 16px;
`;

/**
 * Scroll region for everything below the page header.
 *
 * The shared reports Container is `height: 100vh; display: flex;
 * flex-direction: column`, so the page never scrolls on its own — each
 * report has to own a scrolling child (Bench does it with Content >
 * MainSection). Filters scroll away with the results, same as Bench;
 * the Hide-filters toggle stays reachable because it lives in the header.
 *
 * The 4px padding / -4px margin pair gives the cards' box-shadows room at
 * the scroll edges without shifting them or widening the column.
 */
export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px;
  margin: -4px;
`;

/**
 * Collapsible filter bar.
 *
 * Uses the CSS grid 0fr → 1fr technique rather than a max-height cap: it
 * animates to the content's AUTO height, so there is no number to
 * outgrow. Earlier attempts with max-height clipped the bar whenever the
 * role catalog rendered taller than the guess, and measuring the content
 * raced with the catalog loading.
 *
 *   FilterBar  — the animated grid row (1fr open, 0fr closed)
 *   FilterBarClip — clips during the transition (must be padding-free)
 *   FilterBarInner — the actual padded content
 */
export const FilterBar = styled.div`
  display: grid;
  grid-template-rows: ${props => (props.$show ? '1fr' : '0fr')};
  background: white;
  border-radius: 8px;
  box-shadow: ${props => (props.$show ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none')};
  margin-bottom: ${props => (props.$show ? '20px' : '0')};
  opacity: ${props => (props.$show ? 1 : 0)};
  transition: grid-template-rows 0.3s ease, opacity 0.3s ease,
              margin-bottom 0.3s ease, box-shadow 0.3s ease;
`;

export const FilterBarClip = styled.div`
  overflow: hidden;
  min-height: 0;
`;

export const FilterBarInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px;
`;

export const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
`;

export const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

/**
 * Results card. Grouping lives in its toolbar rather than the filter bar:
 * it is a VIEW control (how results are presented), not a filter (which
 * results match) — same reasoning as the Certifications report's
 * By Individual / By Issuer toggle. It also stays reachable when the
 * filter bar is collapsed.
 */
export const ResultsCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

export const ResultsToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 24px;
  border-bottom: 1px solid #e2e8f0;
`;

/**
 * Segmented control for the grouping dimensions — same visual language as
 * the Certifications report's By Individual / By Issuer toggle. Multi-select
 * here (up to 3) rather than single-select, but the affordance is identical.
 */
export const GroupToggle = styled.div`
  display: flex;
  gap: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  width: fit-content;
`;

export const GroupToggleButton = styled.button`
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  background: ${props => (props.$active ? COLORS.PRIMARY : 'white')};
  color: ${props => (props.$active ? 'white' : '#64748B')};

  &:hover {
    background: ${props => (props.$active ? COLORS.PRIMARY : '#f1f5f9')};
  }

  &:not(:last-child) {
    border-right: 1px solid #e2e8f0;
  }
`;

export const ToolbarLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 4px;
`;

/**
 * Placeholder area for the loading / empty states. LoadingSpinner's own
 * container is height:100%, which collapses to content height inside an
 * auto-height card — leaving the spinner cramped and its glow clipped by
 * the card's overflow:hidden. A definite min-height gives it room and
 * keeps the card from jumping as it swaps loading → results → empty.
 */
export const ResultsPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  padding: 24px;
`;

export const TableWrapper = styled.div`
  overflow: auto;

  /* The header needs to read as a header: in a tree table the indentation
     already carries hierarchy, so an unstyled head row blends into the
     first data row (which is itself a top-level node). Weight and a rule
     do that job — no fill, which would only echo the page background and
     make the card look like it had a gap at the top. */
  thead th {
    color: #475569;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.02em;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
  }
`;

export const TreeRow = styled.tr`
  cursor: ${props => (props.$expandable ? 'pointer' : 'default')};
  background: ${props => (props.$notSet ? '#fafafa' : 'white')};
  color: ${props => (props.$notSet ? '#94a3b8' : 'inherit')};
  &:hover {
    background: #f8fafc;
  }
`;

export const IndentedName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding-left: ${props => props.$depth * 22}px;
`;

export const CountCell = styled.td`
  text-align: right;
  font-variant-numeric: tabular-nums;
  padding: 8px 16px !important;
`;

export const EmptyNote = styled.div`
  text-align: center;
  color: #64748b;
`;
